import './index.css';
import LoginComponent from './components/login-component';
import { DashboardComponent } from './components/dashboard-component';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import ErrorPage from './components/error-page';
import { SignUpComponent } from './components/signup-page-component';
import { ForgotPasswordComponent } from './components/forgot-password-component';
import { TermsOfServiceComponent } from './components/terms-of-service-component';
import { ProfileComponent } from './components/profile-component';
import { CompanyComponent } from './components/company-component';
import { ChangelogComponent } from './components/change-log-component';
import { PrivacyPolicyComponent } from './components/privacy-policy-component';
import { MainBackground } from './components/main-background';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Conversation } from './api/models/Conversation';
import { createCheckoutSession, getUser } from './api/thunks/users';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { TryIt } from './components/try-it-component';
import { createConversation, createConversationShareLink, deleteConversation, deleteConversationShareLink, getAllConversations, getAllSupportedTickers, getConversation, getConversationWithShareLink, updateConversation } from './api/thunks/conversations';
import { setNotification } from './store/notifications-reducer';
import { PricingComponent } from './components/pricing-component';
import { ComparisonComponent } from './components/comparison-component';
import ImageModal, { ImgModalState } from './components/image-modal';
import { CodeModal } from './components/code-modal';
import { BundledLanguage, BundledTheme, createHighlighter, HighlighterGeneric } from "shiki";
import { EarningsCalendarComponent } from './components/earnings-calendar-component';
import { EarningsCalendarItem } from './api/models/EarningsCalendar';
import axios from 'axios';
import { v4 } from 'uuid';
import { ArtifactsPage } from './components/artifacts-component';
import { Artifact } from './api/models/Artifact';
import { createArtifact, deleteArtifact, getAllArtifacts } from './api/thunks/artifacts';
import { WorkflowsPage } from './components/workflows-page';
import { getAllWorkflows } from './api/thunks/workflows';
import { Workflow } from './api/models/Workflows';
import { getAssistants } from './api/thunks/assistants';
import { Assistant } from './api/models/Assistant';
import CongressionalDashboard from './components/congressional-dashboard';
import { Secret } from './api/models/Secret';
import { getSecrets } from './api/thunks/secrets';
import { BlogPage } from './components/blog-page';
import { BlogPost, getAllBlogPosts } from './api/thunks/blogs';
import { BlogPostPage } from './components/blog-post-page';
import { LearningCenterPage } from './components/learning-center-page';
import { CaseStudyPage } from './components/case-study-page';
import { EventsPage } from './components/events-page';
import { HeaderNavComponent } from './components/header-nav-compontent';
import { SplashPageRouter } from './components/splash-page-router';
import { CheckoutComponent } from './components/checkout-component';
import { AntiScrapingComponent } from './components/anti-scraping-component';
import { Message } from './api/models/Message';
import { safeGetCredentials } from './utils/auth';
import { setCredentials } from './store/user-reducer';
import NotificationPopup from './components/notification-popup-component';

const DEFAULT_ARTIFACT_PAGE_SIZE = 12;

export function App() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const credentials = useAppSelector((state) => state.user.credentials);

  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [earningsCalendar, setEarningsCalendar] = useState<EarningsCalendarItem[]>([]);

  const [imgModalState, setImgModalState] = useState<ImgModalState>({
    isImageModalOpen: false,
    modalImageSrc: '',
  });

  const [highlighter, setHighlighter] = useState<HighlighterGeneric<BundledLanguage, BundledTheme>>()
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCode, setCurrentCode] = useState<string | null>(null);

  const [convo, setConvo] = useState<Conversation | undefined>();
  const convoRef = useRef(convo);
  const [allConvos, setAllConvos] = useState<Conversation[]>([]);
  const [supportedTickers, setSupportedTickers] = useState<string[]>([]);
  const [shouldShowSpotlight, setShouldShowSpotlight] = useState<boolean>(allConvos.length === 0);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [artifactPagination, setArtifactPagination] = useState({
    page: 1,
    pageSize: DEFAULT_ARTIFACT_PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasMore: false,
  });
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isShareMode, setIsShareMode] = useState(false);
  const [userSecrets, setUserSecrets] = useState<Secret[]>([]);
  const [messageInProgress, setMessageInProgress] = useState(false);

  // Buffer for message and reasoning deltas by conversation id
  const messageDeltaBuffers = useRef<{ [cid: string]: { [seq: number]: string } }>({});
  const reasoningDeltaBuffers = useRef<{ [cid: string]: { [seq: number]: string } }>({});

  const websocket = useRef<WebSocket>()
  const lastMessageSentTime = useRef<Date | undefined>(undefined);
  let wsPongTimeout: NodeJS.Timeout;
  const handleCreateConversation = async (value: string) => {
    if (!credentials) {
      dispatch(setNotification({ type: 'info', message: 'Please log in to create a conversation' }));
      return
    };
    createConversation({ name: value, version: 'v3' }, credentials.accessToken, dispatch).then((res) => {
      if (res?.id) {
        window.location.href = '/dashboard?conversationId=' + res.id;
      }
    }).catch(() => {
      dispatch(setNotification({ type: 'error', message: 'Failed to create conversation' }));
    });
  }

  const openCodeModal = (code: string) => {
    setCurrentCode(code);
    setIsModalOpen(true);
  };

  const closeCodeModal = () => {
    setCurrentCode(null);
    setIsModalOpen(false);
  };

  const handleDeleteArtifact = (id: string) => {
    if (!credentials) return;

    deleteArtifact(id, credentials.accessToken).then(() => {
      dispatch(setNotification({ type: 'success', message: 'Artifact deleted successfully' }));

      const remainingOnPage = artifacts.length - 1;
      const shouldGoToPreviousPage = remainingOnPage <= 0 && artifactPagination.page > 1 && !artifactPagination.hasMore;
      const nextPage = shouldGoToPreviousPage ? artifactPagination.page - 1 : artifactPagination.page;

      loadArtifacts(nextPage, artifactPagination.pageSize);
    }).catch((error) => {
      dispatch(setNotification({ type: 'error', message: error.message }));
    });
  }

  const handleDeleteConversation = async (id: string) => {
    if (!credentials) return;
    deleteConversation(id, credentials.accessToken, dispatch).then(async () => {
      getAllConversations(credentials.accessToken).then((updated) => {
        if (updated.length > 0) {
          window.location.href = `/dashboard?conversationId=${updated[0].id}`;
        } else {
          createConversation({
            version: 'v3'
          }, credentials.accessToken, dispatch).then((res) => {
            if (res?.id) {
              window.location.href = '/dashboard?conversationId=' + res.id;
            }
          }
          ).catch(() => {
            dispatch(setNotification({ type: 'error', message: 'Failed to create new conversation' }));
          });
        }
      }).catch(() => {
        dispatch(setNotification({ type: 'error', message: 'Failed to get conversations' }));
      });
    }).catch(() => {
      dispatch(setNotification({ type: 'error', message: 'Failed to delete conversation' }));
    })
  }

  const handleCreateArtifact = async (type: 'code' | 'image', data: string, hint?: string) => {
    if (!credentials) return;
    createArtifact({ artifactType: type, data: data, hint: hint }, credentials.accessToken).then((id) => {
      dispatch(setNotification({ type: 'success', message: 'Artifact created successfully' }));
      loadArtifacts(1, artifactPagination.pageSize);
    }).catch((error) => {
      dispatch(setNotification({ type: 'error', message: 'Failed to create artifact' }));
    });
  }

  const loadArtifacts = useCallback((page: number, pageSize: number) => {
    if (!credentials) return;

    getAllArtifacts(credentials.accessToken, { page, pageSize }).then((res) => {
      setArtifacts(res.items);
      setArtifactPagination({
        page: res.page,
        pageSize: res.pageSize,
        total: res.total,
        totalPages: res.totalPages,
        hasMore: res.hasMore,
      });
    }).catch(() => {
      dispatch(setNotification({
        message: 'Failed to get artifacts',
        type: 'error'
      }));
    });
  }, [credentials, dispatch]);

  const handleArtifactsPageChange = (page: number) => {
    const normalizedPage = Math.max(1, page);
    loadArtifacts(normalizedPage, artifactPagination.pageSize);
  };

  const handleArtifactsPageSizeChange = (pageSize: number) => {
    const normalizedPageSize = Math.min(Math.max(pageSize, 1), 50);
    setArtifactPagination((prev) => ({
      ...prev,
      page: 1,
      pageSize: normalizedPageSize,
    }));
    loadArtifacts(1, normalizedPageSize);
  };

  const handleDeleteShareLink = (conversationId: string) => {
    console.log('deleting share link ', conversationId)
    if (!credentials) return;
    deleteConversationShareLink(conversationId, credentials.accessToken).then(() => {
      dispatch(setNotification({
        type: 'success',
        message: 'Share link deleted successfully'
      }));

      if (convo && convo.id === conversationId) {
        setConvo((prev) => {
          if (prev) {
            return { ...prev, shareLink: undefined };
          }
          return prev;
        });
      }

      setAllConvos(allConvos.map((c) => {
        if (c.id === conversationId) {
          c.shareLink = undefined;
        }
        return c;
      }));
    }).catch((error) => {
      dispatch(setNotification({
        type: 'error', message: error
      }));
    })
  };

  const handleUpdateConversation = (id: string, name: string) => {
    if (!credentials) return;
    updateConversation(id, name, credentials.accessToken).then(() => {
      dispatch(setNotification({ type: 'success', message: 'Conversation updated successfully' }));
      setAllConvos(allConvos.map((c) => {
        if (c.id === id) {
          c.name = name;
        }
        return c;
      }));

      if (id === convo?.id) {
        setConvo((prev) => {
          if (prev) {
            return { ...prev, name: name };
          }
          return prev;
        });
      }
    }).catch(() => {
      dispatch(setNotification({ type: 'error', message: 'Failed to update conversation' }));
    });
  }

  const handleCreateShareLink = async (conversationId: string) => {
    if (!credentials || !convo) {
      return;
    }
    const c = allConvos.find(e => e.id === conversationId);
    if (convo?.shareLink) {
      navigator.clipboard.writeText(convo?.shareLink);
      dispatch(setNotification({
        message: 'Link copied to clipboard',
        type: 'success'
      }));
      return;
    }
    createConversationShareLink(conversationId, credentials?.accessToken).then((res) => {
      setConvo({ ...convo, shareLink: res })
      setAllConvos(allConvos.map((c) => {
        if (c.id === conversationId) {
          return { ...c, shareLink: res }
        }
        return c;
      }));

      navigator.clipboard.writeText(res).then(() => {
        dispatch(setNotification({
          message: 'Link created and copied to clipboard',
          type: 'success'
        }));
      }).catch((e) => {
        dispatch(setNotification({
          message: 'Share Link: ' + res,
          type: 'success'
        }));
      });
    }).catch((e) => {
      dispatch(setNotification({
        message: 'Failed to create share link',
        type: 'error'
      }))
    })
  }

  const router = createBrowserRouter([
    {
      path: "/health",
      element: <div>Healthy</div>,
    },
    {
      path: "/:variant?",
      element: <SplashPageRouter />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/company",
      element: <CompanyComponent />
    },
    {
      path: "/changelog",
      element: <ChangelogComponent />
    },
    {
      path: '/terms-of-service',
      element: <TermsOfServiceComponent></TermsOfServiceComponent>,
    },
    {
      path: 'privacy-policy',
      element: <PrivacyPolicyComponent></PrivacyPolicyComponent>,
    },
    {
      path: "/profile",
      element:
        <MainBackground>
          <ProfileComponent handleCreateShareLink={handleCreateShareLink} handleUpdateConversation={handleUpdateConversation} convo={convo} convos={allConvos} handleCreateConversation={handleCreateConversation} handleDeleteConversation={handleDeleteConversation} supportedTickers={supportedTickers} handleDeleteShareLink={handleDeleteShareLink} secrets={userSecrets} setSecrets={setUserSecrets} />
        </MainBackground>,
    },
    {
      path: '/signup',
      element: <SignUpComponent></SignUpComponent>,
      errorElement: <ErrorPage />,
    },
    {
      path: '/password-reset',
      element: <ForgotPasswordComponent />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/login",
      element: <LoginComponent></LoginComponent>,
      errorElement: <ErrorPage />,
    },
    {
      path: "/dashboard",
      element:
        <>
          <NotificationPopup />
          <ImageModal
            imgName=''
            isOpen={imgModalState.isImageModalOpen}
            imageSrc={imgModalState.modalImageSrc}
            onClose={() => setImgModalState({ ...imgModalState, isImageModalOpen: false })}
          />
          {currentCode && (
            <CodeModal
              isOpen={isModalOpen}
              code={currentCode}
              onClose={closeCodeModal}
              highlighter={highlighter}
              language='python'
              openCodeModal={openCodeModal}
              createArtifact={(type, data) => handleCreateArtifact(type, data, undefined)}
            />
          )}
          {/* <SpotlightSearchOverlay placeholder='Search companies by ticker...' searchValues={supportedTickers} handleSearchValueClicked={handleCreateConversation} show={shouldShowSpotlight} /> */}
          {/* <MainBackground> */}
            <DashboardComponent
              handleUpdateConversation={handleUpdateConversation}
              isShareMode={isShareMode}
              setLastMessageSentTime={() => {
                lastMessageSentTime.current = new Date();
              }}
              setAllConvos={setAllConvos}
              handleCreateArtifact={handleCreateArtifact}
              handleCreateConversation={handleCreateConversation}
              supportedTickers={supportedTickers}
              convos={allConvos}
              openCodeModal={openCodeModal}
              setImgModalState={setImgModalState}
              convo={convo} setConvo={setConvo}
              handleDeleteConversation={handleDeleteConversation}
              handleDeleteShareLink={handleDeleteShareLink}
              handleCreateShareLink={handleCreateShareLink}
              messageInProgress={messageInProgress}
              onWorkflowCreated={() => {
                if (credentials?.accessToken) {
                  getAllWorkflows(credentials.accessToken).then((workflows) => {
                    setWorkflows(workflows);
                  }).catch((err) => {
                    console.error('Failed to refresh workflows after creation', err);
                  });
                }
              }}
            />
          {/* </MainBackground> */}
        </>,
      errorElement: <ErrorPage />,
    },
    {
      path: '/try-it',
      element: <MainBackground>
        <TryIt showNav={true} showTabs={true} />
      </MainBackground>
    },
    {
      path: '/pricing',
      element:
        <MainBackground>
          <PricingComponent showNav={true} />
        </MainBackground>
    },
    {
      path: '/pq-v-chatgpt',
      element:
        <MainBackground >
          <ComparisonComponent showNav={true} />
        </MainBackground>,
    },
    {
      path: '/earnings-calendar',
      element: <MainBackground>
        <HeaderNavComponent />
        <EarningsCalendarComponent calendar={earningsCalendar} />
      </MainBackground>
    },
    {
      path: '/artifacts',
      element: <MainBackground>
        <ArtifactsPage
          handleUpdateConversation={handleUpdateConversation}
          artifacts={artifacts}
          pagination={artifactPagination}
          onPageChange={handleArtifactsPageChange}
          onPageSizeChange={handleArtifactsPageSizeChange}
          convo={convo}
          convos={allConvos}
          handleCreateConversation={handleCreateConversation}
          supportedTickers={supportedTickers}
          highlighter={highlighter}
          handleDeleteArtifact={handleDeleteArtifact}
          handleDeleteConversation={handleDeleteConversation}
          handleDeleteShareLink={handleDeleteShareLink}
          handleCreateShareLink={handleCreateShareLink}
        />
      </MainBackground>
    },
    {
      path: '/workflows',
      element: <MainBackground>
        <WorkflowsPage
          handleUpdateConversation={handleUpdateConversation}
          workflows={workflows}
          setWorkflows={setWorkflows}
          convo={convo}
          convos={allConvos}
          assistants={assistants}
          handleCreateConversation={handleCreateConversation}
          handleDeleteConversation={handleDeleteConversation}
          supportedTickers={['all', ...supportedTickers]}
          handleDeleteShareLink={handleDeleteShareLink}
          handleCreateShareLink={handleCreateShareLink}
          userSecrets={userSecrets}
        />
      </MainBackground>
    },
    {
      path: '/test',
      element: <MainBackground>
        <CongressionalDashboard
          onDataRefresh={() => {
            // Implement data refresh logic here
            console.log('Refreshing congressional data...');
          }}
        />
      </MainBackground>
    },
    {
      path: '/blogs',
      element: <MainBackground>
        <BlogPage posts={blogPosts} />
      </MainBackground>
    },
    {
      path: '/blogs/:postId/:slug',
      element:
        <MainBackground>
          <BlogPostPage allPosts={blogPosts} />
        </MainBackground>
    },
    {
      path: '/learning-center',
      element: <MainBackground>
        <LearningCenterPage />
      </MainBackground>
    },
    {
      path: '/case-study',
      element: <MainBackground>
        <CaseStudyPage />
      </MainBackground>
    },
    {
      path: '/events',
      element: <MainBackground>
        <EventsPage />
      </MainBackground>
    },
    {
      path: "/checkout",
      element: <MainBackground>
        <CheckoutComponent />
      </MainBackground>,
    },
    {
      path: "/anti-scraping",
      element: <MainBackground>
        <AntiScrapingComponent />
      </MainBackground>,
    },
    {
      path: "/congressional-data",
      element: <MainBackground>
        <CongressionalDashboard
          onDataRefresh={() => {
            // Implement data refresh logic here
            console.log('Refreshing congressional data...');
          }}
        />
      </MainBackground>,
    }
  ]);

  const snakeCaseToTitleCase = (str: string) => {
    return str.replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize the first letter of each word
  };
  const camelCaseToTitleCase = (str: string) => {
    return str.replace(/([A-Z])/g, ' $1').replace(/^./, function (str) { return str.toUpperCase(); });
  };

  const setupWebsocket = () => {
    if (window.location.pathname !== '/dashboard' && window.location.pathname !== '/workflows') {
      return;
    }

    // Clean up existing websocket and interval
    if (websocket.current) {
      websocket.current.close();
    }
    clearInterval(wsPongTimeout);

    if (websocket.current === undefined || websocket.current?.readyState !== WebSocket.OPEN) {
      websocket.current = new WebSocket(process.env.REACT_APP_API_URL + "/v1/events");
      websocket.current.onopen = () => {
        console.log("websocket connected, sending auth message");
        if (!websocket.current || !credentials || websocket.current.readyState !== WebSocket.OPEN) {
          console.log("no websocket or no credentials ", websocket.current, credentials);
          return;
        }

        const creds = safeGetCredentials();
        const accessToken = creds?.accessToken || credentials?.accessToken;

        if (!accessToken) {
          console.error('No access token available for WebSocket auth');
          return;
        }

        websocket.current.send(JSON.stringify({ type: "authorization", data: accessToken }));

        wsPongTimeout = setInterval(() => {
          if (websocket.current && websocket.current.readyState === WebSocket.OPEN) {
            console.log('ponging')
            websocket.current.send(JSON.stringify({ type: "pong" }));
          }
        }, 500);
      };

      websocket.current.onmessage = async (event) => {
        const msg = JSON.parse(event.data);

        if (msg.type === 'ping' && websocket.current?.readyState === WebSocket.OPEN) {
          try {
            websocket.current?.send(`{"type":"pong"}`)
          } catch (e) {
            console.log('error sending pong', e);
          }
          return;
        }

        if (!credentials) {
          return;
        }

        console.log("websocket message", msg);
        const query = new URLSearchParams(window.location.search);
        switch (msg.type) {
          case "conversation_evicted_from_cache":
            if (!msg.data.metadata['conversation_id'] || msg.data.metadata['conversation_id'] !== convo?.id) return;
            var updatedConvo = await getConversation(query.get('conversationId') || msg.data.metadata['conversation_id'], credentials.accessToken);
            var runCompleteAllConvosNew = await getAllConversations(credentials.accessToken); // because sometimes the name or ticker changes and then the convo list would be out of date
            setAllConvos(runCompleteAllConvosNew);
            setConvo(updatedConvo);
            break;
          case "run_completed":
            console.log('received conversation updated', msg);
            lastMessageSentTime.current = undefined;
            var updatedConvo = await getConversation(query.get('conversationId') || msg.data.metadata['conversation_id'], credentials.accessToken);
            var runCompleteAllConvosNew = await getAllConversations(credentials.accessToken); // because sometimes the name or ticker changes and then the convo list would be out of date
            setAllConvos(runCompleteAllConvosNew);
            setConvo(updatedConvo);
            setMessageInProgress(false);
            break;
          case "message_delta":
            if (!messageInProgress) setMessageInProgress(true);
            var cid = msg.data.metadata["conversation_id"]
            var seqStr = msg.data.metadata["sequence_number"];
            var seq = Number(seqStr);
            var delta = msg.data.metadata["delta"];
            if (!messageDeltaBuffers.current[cid]) messageDeltaBuffers.current[cid] = {};
            messageDeltaBuffers.current[cid][seq] = delta;
            if (cid === convoRef.current?.id && convoRef.current && convoRef.current.messages) {
              const deepCopy = JSON.parse(JSON.stringify(convoRef.current)) as Conversation;
              const lastMessage = deepCopy!.messages![deepCopy!.messages!.length - 1];
              if (!lastMessage) {
                // create a message
                return
              }
              if (lastMessage.role !== Message.RoleEnum.Assistant) {
                const newLastMessage: Message = {
                  id: "in-flight-message",
                  role: Message.RoleEnum.Assistant,
                  content: [
                    {
                      text: '',
                      fileCitations: [],
                      images: []
                    }
                  ],
                }
                // Concatenate all deltas in order
                const deltas = messageDeltaBuffers.current[cid];
                const ordered = Object.keys(deltas).map(Number).sort((a, b) => a - b).map(k => deltas[k]);
                if (newLastMessage.content && newLastMessage.content[0]) {
                  newLastMessage.content[0].text = ordered.join("");
                }
                deepCopy!.messages!.push(newLastMessage)
              } else {
                // Concatenate all deltas in order
                const deltas = messageDeltaBuffers.current[cid];
                const ordered = Object.keys(deltas).map(Number).sort((a, b) => a - b).map(k => deltas[k]);
                const lastMessage = deepCopy!.messages![deepCopy!.messages!.length - 1];
                if (lastMessage.content && lastMessage.content.length > 0 && lastMessage.content[0]) {
                  lastMessage.content[0].text = ordered.join("");
                }
              }
              setConvo(deepCopy);
            }
            break;
          case "reasoning_delta":
            if (!messageInProgress) setMessageInProgress(true);
            var cid = msg.data.metadata["conversation_id"]
            var seqStr = msg.data.metadata["sequence_number"];
            var seq = Number(seqStr);
            var delta = msg.data.metadata["delta"];
            if (!reasoningDeltaBuffers.current[cid]) reasoningDeltaBuffers.current[cid] = {};
            reasoningDeltaBuffers.current[cid][seq] = delta;
            if (cid === convoRef.current?.id && convoRef.current && convoRef.current.messages) {
              const deepCopy = JSON.parse(JSON.stringify(convoRef.current)) as Conversation;
              const lastMessage = deepCopy!.messages![deepCopy!.messages!.length - 1];
              if (!lastMessage) {
                // create a message
                return
              }
              if (lastMessage.role !== Message.RoleEnum.Assistant) {
                const newLastMessage: Message = {
                  id: "in-flight-message",
                  role: Message.RoleEnum.Assistant,
                  content: [
                    {
                      text: '',
                      fileCitations: [],
                      images: []
                    }
                  ],
                  thinkingSummary: ''
                }
                // Concatenate all deltas in order
                const deltas = reasoningDeltaBuffers.current[cid];
                const ordered = Object.keys(deltas).map(Number).sort((a, b) => a - b).map(k => deltas[k]);
                newLastMessage.thinkingSummary = ordered.join("");
                deepCopy!.messages!.push(newLastMessage)
              } else {
                // Concatenate all deltas in order
                const deltas = reasoningDeltaBuffers.current[cid];
                const ordered = Object.keys(deltas).map(Number).sort((a, b) => a - b).map(k => deltas[k]);
                const lastMessage = deepCopy!.messages![deepCopy!.messages!.length - 1];
                if (lastMessage) {
                  lastMessage.thinkingSummary = ordered.join("");
                }
              }
              setConvo(deepCopy);
            }
            break;
          case "message_completed":
            console.log('received message completed');
            lastMessageSentTime.current = undefined;
            // Clear delta buffers for this conversation
            if (msg.data.metadata && msg.data.metadata['conversation_id']) {
              delete messageDeltaBuffers.current[msg.data.metadata['conversation_id']];
              delete reasoningDeltaBuffers.current[msg.data.metadata['conversation_id']];
            }
            const updatedConvoMessage = await getConversation(query.get('conversationId') || msg.data.metadata['conversation_id'], credentials.accessToken);
            setConvo(updatedConvoMessage);
            const messageCompletedAllConvosNew = await getAllConversations(credentials.accessToken); // because sometimes the name or ticker changes and then the convo list would be out of date
            setAllConvos(messageCompletedAllConvosNew);
            break;
          case "free_credit_used":
            console.log('received free credit used');
            dispatch(getUser());
            break;
          case "function_call":
            const funcName = msg.data.function_call_details.name;
            let funcDesc = snakeCaseToTitleCase(funcName);
            let jsonArgs = JSON.parse(msg.data.function_call_details.args);
            if (funcName === 'get_financial_ratio') {
              funcDesc = `Computing ${jsonArgs.fiscalPeriod}-${jsonArgs.fiscalYear} ${snakeCaseToTitleCase(jsonArgs.ratio)}`;
            } else if (funcName === 'get_earnings_statement_item') {
              funcDesc = `Looking up earnings data ${camelCaseToTitleCase(jsonArgs.item)} ${jsonArgs.fiscalPeriod}-${jsonArgs.fiscalYear}`;
            } else if (funcName === 'get_schema') {
              funcDesc = `Looking up ${snakeCaseToTitleCase(jsonArgs.schemaName)}`;
            } else if (funcName === 'get_segments_or_kpi_info') {
              funcDesc = `Getting ${snakeCaseToTitleCase(jsonArgs.segmentOrKpiName)} ${jsonArgs.fiscalPeriod}-${jsonArgs.fiscalYear}`;
            }

            dispatch(setNotification({
              message: funcDesc,
              type: 'info'
            }));
            break;
          case "workflow_update":
            dispatch(setNotification({
              message: msg.data.message,
              type: 'info'
            }));

            getAllWorkflows(credentials.accessToken).then((workflows) => {
              console.log('got all workflows after workflow updated message');
              setWorkflows(workflows);
            }).catch((err) => {
              console.log('failed to get workflows when websocket update came through', err);
              dispatch(setNotification({
                message: 'Failed to get workflows',
                type: 'error'
              }))
            });

            break;
        }
      };

      websocket.current.onclose = () => {
        console.log("websocket closed, reconnecting in 5 seconds");
        clearInterval(wsPongTimeout);
        setTimeout(() => {
          websocket.current = undefined;
          clearInterval(wsPongTimeout);
          setupWebsocket();
        }, 5000);
      }
    }
  }

  const getEarningsCalendar = async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + '/v1/earnings_calendar',
        {
          headers: {
            'X-Request-ID': v4()
          }
        }
      );

      if (response.status !== 200) {
        // dispatch(setNotification({
        //   message: "Unable to get earnings calendar",
        //   type: 'error'
        // }))

        return;
      }

      setEarningsCalendar(response.data as EarningsCalendarItem[]);
    } catch (e) {
      console.log(e)
      // dispatch(setNotification({
      //   message: "Unable to get earnings calendar",
      //   type: 'error'
      // }))
    }
  }

  const handleDemoLogin = () => {
    const query = new URLSearchParams(window.location.search);
    const idToken = query.get("id_token");
    const refreshToken = query.get("refresh_token");
    const demoMigrated = query.get("demo_migrated");

    if (idToken && refreshToken) {
      try {
        console.log('Processing OAuth tokens', { idToken: idToken.substring(0, 20) + '...', refreshToken: refreshToken.substring(0, 20) + '...' });

        // Clean up URL first to prevent reprocessing
        query.delete("id_token");
        query.delete("refresh_token");
        query.delete("demo_migrated");
        window.history.replaceState({}, document.title, window.location.pathname + (query.toString() ? '?' + query.toString() : ''));

        // Set the new credentials
        console.log('setting creds')
        dispatch(setCredentials({ accessToken: idToken, refreshToken }));
        console.log('creds set to url vals')

        // Show success message if this was a demo migration
        if (demoMigrated === 'true') {
          dispatch(setNotification({
            message: 'Successfully upgraded your demo account with Google!',
            type: 'success'
          }));
        }

      } catch (error) {
        console.error('Failed to set credentials from OAuth:', error);
        dispatch(setNotification({
          message: 'Failed to process OAuth response',
          type: 'error'
        }));
      }
    }
  }

  useEffect(() => {
    if (!credentials) return;

    const accessToken = credentials.accessToken;

    localStorage.setItem('credentials', JSON.stringify(credentials));

    setupWebsocket();
    dispatch(getUser());
    const query = new URLSearchParams(window.location.search);
    const conversationId = query.get('conversationId');

    if (query.get("redirect") === "checkout" && query.get('price_id') !== null) {
      dispatch(createCheckoutSession({ productCode: query.get('price_id')! })).then((checkoutRes) => {
        console.log('checkoutRes ', checkoutRes)
        if (checkoutRes.meta.requestStatus === 'fulfilled') {
          window.location.href = (checkoutRes.payload as any).redirectUrl;
        } else {
          alert('Failed to create checkout session 1' + JSON.stringify(checkoutRes));
          return
        }
      }).catch((err) => {
        console.log('err ', err);
        alert('Failed to create checkout session 2');
        return
      })

      return;
    }

    getAllConversations(accessToken).then((res) => {
      setAllConvos(res);
      if (!conversationId && res.length > 0) {
        query.set('conversationId', res[0].id);
        window.location.href = window.location.pathname + '?' + query.toString();
      }
    }).catch((err) => {
      dispatch(setNotification({
        message: 'Failed to get conversations',
        type: 'error'
      }));
    });

    if (conversationId && window.location.href.includes('dashboard')) {
      getConversation(conversationId, accessToken).then((res) => {
        setConvo(res);
      }).catch((err) => {
        dispatch(setNotification({
          message: 'Failed to get conversation',
          type: 'error'
        }));
      });
    }

    getAllSupportedTickers({ accessToken }).then((res) => {
      setSupportedTickers(res);
    }).catch((err) => {
      dispatch(setNotification({
        message: 'Failed to get supported tickers',
        type: 'error'
      }));
    });

    setArtifactPagination({
      page: 1,
      pageSize: DEFAULT_ARTIFACT_PAGE_SIZE,
      total: 0,
      totalPages: 1,
      hasMore: false,
    });
    loadArtifacts(1, DEFAULT_ARTIFACT_PAGE_SIZE);

    getAllWorkflows(accessToken).then((workflows) => {
      setWorkflows(workflows);
    }).catch(() => {
      dispatch(setNotification({
        message: 'Failed to get workflows',
        type: 'error'
      }))
    });

    getAssistants(accessToken).then((assistants) => {
      setAssistants(assistants);
    }).catch(() => {
      dispatch(setNotification({
        message: 'Failed to get assistants',
        type: 'error'
      }))
    });

    getSecrets(accessToken).then((secrets) => {
      setUserSecrets(secrets);
    }).catch(() => {
      dispatch(setNotification({
        message: 'Failed to get user secrets',
        type: 'error'
      }))
    })
  }, [credentials, dispatch, loadArtifacts])

  useEffect(() => {
    if (user?.hasActiveSubscription !== true) {
      return;
    }

    console.log("sending purchase conversion event")
    let value = 1; // pay as you go will be considered $1
    if (user.plan === 'pro') {
      value = 20;
    } else if (user.plan === 'premium') {
      value = 49;
    }
    gtag('event', 'conversion', {
      'send_to': 'AW-16667259928/KabTCJq7hpQaEJjoyIs-',
      'value': value,
      'currency': 'USD',
      'transaction_id': user?.id
    });
    console.log('done sending purchase conversion event');
  }, [user])

  useEffect(() => {
    convoRef.current = convo;
  }, [convo]);

  // Handle OAuth tokens on mount - separate from credentials useEffect to prevent infinite loop
  useEffect(() => {
    handleDemoLogin();
  }, []); // Run only once on mount

  useEffect(() => {

    getAllBlogPosts().then((posts) => {
      setBlogPosts(posts);
    }).catch((err) => {
      console.log('failed to get blog posts', err);
      dispatch(setNotification({
        message: 'Failed to get blog posts',
        type: 'error'
      }))
    })

    getEarningsCalendar()

    setInterval(() => {
      if (credentials && lastMessageSentTime.current !== undefined && convoRef.current !== undefined) {
        const query = new URLSearchParams(window.location.search);
        const now = new Date();
        const elapsed = now.getTime() - lastMessageSentTime.current.getTime();
        if (elapsed >= 10_000) {
          console.log("10s elapsed so manually re-fetching convo")
          lastMessageSentTime.current = undefined;
          getConversation(query.get('conversationId') || convoRef.current.id, credentials.accessToken).then((res) => {
            console.log("got convo after time since last message sent > 10s")
            setConvo(res)
          }).catch((err) => {
            console.error('err fetching convo after last sent time elapsed ', err)
          })
        }
      }
    }, 2000);

    createHighlighter({ themes: ["github-dark"], langs: ["python"] }).then((h) => {
      setHighlighter(h);
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === " " || e.key === 's')) {
        e.preventDefault();
        setShouldShowSpotlight((prev) => !prev)
      }

      if (e.key === 'Escape' && shouldShowSpotlight) {
        setShouldShowSpotlight(false);
      }
    };

    const query = new URLSearchParams(window.location.search);
    if (window.location.href.includes('dashboard') && query.get('share') !== null) {
      const shareLinkId = query.get('share');
      if (shareLinkId) {
        setIsShareMode(true);
        getConversationWithShareLink(shareLinkId).then((res) => {
          setConvo(res);
        }).catch((err) => {
          dispatch(setNotification({
            message: 'Failed to get shared conversation',
            type: 'error'
          }));
        });

  getAllSupportedTickers({ shareLinkToken: shareLinkId }).then((res) => {
          setSupportedTickers(res);
        }).catch((err) => {
          dispatch(setNotification({
            message: 'Failed to get supported tickers',
            type: 'error'
          }));
        });
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Clean up websocket and interval on unmount
      if (websocket.current) {
        websocket.current.close();
      }
      clearInterval(wsPongTimeout);
    };
  }, []);

  return (
    <RouterProvider router={router} />
  );
}

